import React, { useState, useCallback, useEffect } from 'react';
import { Upload, X, Check } from 'lucide-react';
import { read, utils } from 'xlsx';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import ColumnMapper from './import/ColumnMapper';
import ContactPreview from './import/ContactPreview';
import { validateContacts } from '../../utils/importValidation';
import { findDuplicates } from '../../utils/duplicateDetection';
import { parseAndValidateDate } from '../../utils/dateUtils';
import type { PreviewContact, ColumnMapping, ValidationError } from '../../types/import';

interface ImportContactsProps {
  onClose: () => void;
  onSuccess: () => void;
}

export default function ImportContacts({ onClose, onSuccess }: ImportContactsProps) {
  const [previewData, setPreviewData] = useState<PreviewContact[]>([]);
  const [rawData, setRawData] = useState<any[]>([]);
  const [columnMapping, setColumnMapping] = useState<ColumnMapping>({});
  const [availableColumns, setAvailableColumns] = useState<string[]>([]);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>([]);
  const [duplicates, setDuplicates] = useState<Set<string>>(new Set());
  const { user } = useAuth();

  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [onClose]);

  const handleDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    handleFile(file);
  }, []);

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
  };

  const handleFile = async (file: File) => {
    try {
      const data = await file.arrayBuffer();
      const workbook = read(data);
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      const jsonData = utils.sheet_to_json(worksheet);
      
      setRawData(jsonData);
      setAvailableColumns(Object.keys(jsonData[0] || {}).map(col => col.trim()));
      
      // Try to auto-map columns
      const mapping: ColumnMapping = {};
      Object.keys(jsonData[0] || {}).forEach(column => {
        const normalizedColumn = column.toLowerCase().trim().replace(/\s+/g, '_');
        if (normalizedColumn.includes('first') && normalizedColumn.includes('name')) {
          mapping.first_name = column;
        } else if (normalizedColumn.includes('last') && normalizedColumn.includes('name')) {
          mapping.last_name = column;
        }
      });
      setColumnMapping(mapping);
      
      setError(null);
    } catch (err) {
      setError('Error reading file. Please make sure it\'s a valid Excel or CSV file.');
    }
  };

  const updateMapping = (field: string, column: string) => {
    setColumnMapping(prev => ({
      ...prev,
      [field]: column
    }));
  };

  const processData = async () => {
    if (!rawData.length) return;

    const processed: PreviewContact[] = rawData.map(row => ({
      first_name: (row[columnMapping.first_name] || '').toString().trim(),
      last_name: (row[columnMapping.last_name] || '').toString().trim(),
      phone_number: row[columnMapping.phone_number]?.toString().trim() || '',
      work_phone: row[columnMapping.work_phone]?.toString().trim() || '',
      personal_email: row[columnMapping.personal_email]?.toString().trim() || '',
      work_email: row[columnMapping.work_email]?.toString().trim() || '',
      linkedin_url: row[columnMapping.linkedin_url]?.toString().trim() || '',
      birthday: row[columnMapping.birthday] ? parseAndValidateDate(row[columnMapping.birthday].toString().trim()) : null,
      significant_other: row[columnMapping.significant_other]?.toString().trim() || '',
      children_details: row[columnMapping.children_details]?.toString().trim() || ''
    }));

    const errors = validateContacts(processed);
    setValidationErrors(errors);

    if (errors.length === 0) {
      const duplicateSet = await findDuplicates(processed);
      setDuplicates(duplicateSet);
      setPreviewData(processed);
    }
  };

  const handleImport = async () => {
    if (!user || validationErrors.length > 0) return;
    setImporting(true);
    setError(null);

    try {
      const contactsToInsert = previewData.map(contact => ({
        ...contact,
        user_id: user.id,
        phone_number: contact.phone_number || null,
        work_phone: contact.work_phone || null,
        personal_email: contact.personal_email || null,
        work_email: contact.work_email || null,
        linkedin_url: contact.linkedin_url || null,
        birthday: contact.birthday || null,
        significant_other: contact.significant_other || null,
        children_details: contact.children_details || null
      }));

      const { error: importError } = await supabase
        .from('nrm_contacts')
        .insert(contactsToInsert);

      if (importError) throw importError;

      onSuccess();
      onClose();
    } catch (err) {
      setError('Failed to import contacts. Please try again.');
      console.error('Import error:', err);
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4">
      <div className="bg-dark-800 rounded-lg max-w-4xl w-full p-6 space-y-6">
        <div className="flex justify-between items-center">
          <h2 className="text-xl font-semibold">Import Contacts</h2>
          <button 
            onClick={onClose}
            className="text-gray-400 hover:text-white"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {!rawData.length ? (
          <div
            onDrop={handleDrop}
            onDragOver={(e) => e.preventDefault()}
            className="border-2 border-dashed border-dark-600 rounded-lg p-8 text-center space-y-4"
          >
            <Upload className="w-12 h-12 mx-auto text-gray-400" />
            <div>
              <p className="text-lg font-medium">Drag and drop your spreadsheet here</p>
              <p className="text-gray-400">or</p>
              <label className="btn btn-secondary mt-2 cursor-pointer inline-block">
                Browse Files
                <input
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  className="hidden"
                  onChange={handleFileInput}
                />
              </label>
            </div>
            <p className="text-sm text-gray-400">
              Supported formats: Excel (.xlsx, .xls) and CSV files
            </p>
          </div>
        ) : !previewData.length ? (
          <div className="space-y-6">
            <ColumnMapper
              availableColumns={availableColumns}
              mapping={columnMapping}
              onUpdateMapping={updateMapping}
            />
            
            <div className="flex justify-end space-x-4">
              <button 
                onClick={() => {
                  setRawData([]);
                  setColumnMapping({});
                  setValidationErrors([]);
                  setError(null);
                }}
                className="btn btn-secondary"
              >
                Cancel
              </button>
              <button 
                onClick={processData}
                className="btn btn-primary"
              >
                Preview Data
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-6">
            <ContactPreview
              contacts={previewData}
              duplicates={duplicates}
            />
            
            <div className="flex justify-end space-x-4">
              <button 
                onClick={() => {
                  setPreviewData([]);
                  setValidationErrors([]);
                  setError(null);
                }}
                className="btn btn-secondary"
              >
                Back
              </button>
              <button 
                onClick={handleImport}
                disabled={importing}
                className="btn btn-primary"
              >
                {importing ? (
                  <span className="flex items-center">
                    Importing...
                  </span>
                ) : (
                  <span className="flex items-center">
                    <Check className="w-4 h-4 mr-2" />
                    Import Contacts
                  </span>
                )}
              </button>
            </div>
          </div>
        )}

        {error && (
          <div className="bg-dark-700 rounded-lg p-4 mt-4 flex justify-between items-start">
            <p className="text-red-500 text-sm">{error}</p>
            <button 
              onClick={() => setError(null)}
              className="text-gray-400 hover:text-white ml-4"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {validationErrors.length > 0 && (
          <div className="bg-dark-700 rounded-lg p-4 mt-4">
            <div className="flex justify-between items-start mb-2">
              <h3 className="text-red-500 font-medium">Validation Errors</h3>
              <button 
                onClick={() => setValidationErrors([])}
                className="text-gray-400 hover:text-white ml-4"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <ul className="space-y-1">
              {validationErrors.map((error, index) => (
                <li key={index} className="text-sm text-red-400">
                  Row {error.row}: {error.message}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}